import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as adminService from '../../services/admin';

const ApprovalWorkflows = () => {
    const [workflows, setWorkflows] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        workflow_name: '',
        module: '',
        approval_levels: 1,
        level_config: [],
        amount_threshold: '',
        is_active: true
    });

    useEffect(() => {
        loadWorkflows();
        loadUsers();
    }, []);

    const loadWorkflows = async () => {
        try {
            setLoading(true);
            const response = await adminService.listApprovalWorkflows({});
            if (response.status === 'success') {
                setWorkflows(response.data);
            }
        } catch (error) {
            console.error('Error loading workflows:', error);
            toast.error('Failed to load workflows');
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await adminService.listUsers();
            if (response.status === 'success') {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Update level_config when approval_levels changes
        if (name === 'approval_levels') {
            const levels = parseInt(value);
            const newLevelConfig = [];
            for (let i = 0; i < levels; i++) {
                newLevelConfig.push(formData.level_config[i] || {
                    level: i + 1,
                    level_name: `Level ${i + 1}`,
                    approvers: [],
                    require_all: false
                });
            }
            setFormData({
                ...formData,
                approval_levels: levels,
                level_config: newLevelConfig
            });
        }
    };

    const handleLevelConfigChange = (levelIndex, field, value) => {
        const newLevelConfig = [...formData.level_config];
        newLevelConfig[levelIndex] = {
            ...newLevelConfig[levelIndex],
            [field]: value
        };
        setFormData({
            ...formData,
            level_config: newLevelConfig
        });
    };

    const handleApproverToggle = (levelIndex, userId) => {
        const currentApprovers = formData.level_config[levelIndex].approvers;
        const newApprovers = currentApprovers.includes(userId)
            ? currentApprovers.filter(id => id !== userId)
            : [...currentApprovers, userId];

        handleLevelConfigChange(levelIndex, 'approvers', newApprovers);
    };

    const handleNewWorkflow = () => {
        setFormData({
            id: null,
            workflow_name: '',
            module: '',
            approval_levels: 1,
            level_config: [{
                level: 1,
                level_name: 'Level 1',
                approvers: [],
                require_all: false
            }],
            amount_threshold: '',
            is_active: true
        });
        setEditMode(false);
        setShowModal(true);
    };

    const handleEditWorkflow = (workflow) => {
        setFormData({
            id: workflow.id,
            workflow_name: workflow.workflow_name,
            module: workflow.module,
            approval_levels: workflow.approval_levels,
            level_config: workflow.level_config,
            amount_threshold: workflow.amount_threshold || '',
            is_active: workflow.is_active
        });
        setEditMode(true);
        setShowModal(true);
    };

    const handleSaveWorkflow = async () => {
        // Validation
        if (!formData.workflow_name || !formData.module) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Validate that each level has at least one approver
        for (let i = 0; i < formData.level_config.length; i++) {
            if (formData.level_config[i].approvers.length === 0) {
                toast.error(`Please select at least one approver for ${formData.level_config[i].level_name}`);
                return;
            }
        }

        try {
            setLoading(true);
            const data = {
                workflow_name: formData.workflow_name,
                module: formData.module,
                approval_levels: formData.approval_levels,
                level_config: formData.level_config,
                amount_threshold: formData.amount_threshold || null,
                is_active: formData.is_active
            };

            let response;
            if (editMode) {
                response = await adminService.updateApprovalWorkflow(formData.id, data);
            } else {
                response = await adminService.createApprovalWorkflow(data);
            }

            if (response.status === 'success') {
                toast.success(`Workflow ${editMode ? 'updated' : 'created'} successfully`);
                setShowModal(false);
                loadWorkflows();
            } else {
                toast.error(response.message || 'Failed to save workflow');
            }
        } catch (error) {
            console.error('Error saving workflow:', error);
            toast.error('Failed to save workflow');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWorkflow = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This workflow will be deleted permanently',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.deleteApprovalWorkflow(id);
                if (response.status === 'success') {
                    toast.success('Workflow deleted successfully');
                    loadWorkflows();
                } else {
                    toast.error(response.message || 'Failed to delete workflow');
                }
            } catch (error) {
                console.error('Error deleting workflow:', error);
                toast.error('Failed to delete workflow');
            }
        }
    };

    const moduleOptions = [
        'vouchers',
        'budgets',
        'fixed_assets',
        'loans',
        'members',
        'groups',
        'payslips'
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fa fa-sitemap"></i> Approval Workflows
                    </h5>
                    <button className="btn btn-light btn-sm" onClick={handleNewWorkflow}>
                        <i className="fa fa-plus"></i> New Workflow
                    </button>
                </div>
                <div className="card-body">
                    {workflows.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Workflow Name</th>
                                        <th>Module</th>
                                        <th>Levels</th>
                                        <th>Amount Threshold</th>
                                        <th>Status</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workflows.map(workflow => (
                                        <tr key={workflow.id}>
                                            <td>{workflow.workflow_name}</td>
                                            <td>
                                                <span className="badge bg-info text-capitalize">
                                                    {workflow.module}
                                                </span>
                                            </td>
                                            <td>{workflow.approval_levels}</td>
                                            <td>
                                                {workflow.amount_threshold
                                                    ? `₹${parseFloat(workflow.amount_threshold).toLocaleString('en-IN')}`
                                                    : 'N/A'}
                                            </td>
                                            <td>
                                                <span className={`badge ${workflow.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {workflow.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-primary me-2"
                                                    onClick={() => handleEditWorkflow(workflow)}
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteWorkflow(workflow.id)}
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-sitemap fa-3x mb-3"></i>
                            <p>No workflows configured</p>
                            <button className="btn btn-primary" onClick={handleNewWorkflow}>
                                <i className="fa fa-plus"></i> Create First Workflow
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Workflow Modal */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editMode ? 'Edit Workflow' : 'New Workflow'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Workflow Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="workflow_name"
                                            value={formData.workflow_name}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Module *</label>
                                        <select
                                            className="form-select"
                                            name="module"
                                            value={formData.module}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">-- Select Module --</option>
                                            {moduleOptions.map(mod => (
                                                <option key={mod} value={mod}>
                                                    {mod.charAt(0).toUpperCase() + mod.slice(1).replace(/_/g, ' ')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Approval Levels</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="approval_levels"
                                            value={formData.approval_levels}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="5"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Amount Threshold</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="amount_threshold"
                                            value={formData.amount_threshold}
                                            onChange={handleInputChange}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Status</label>
                                        <div className="form-check form-switch mt-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">
                                                {formData.is_active ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <h6 className="mb-3">Approval Levels Configuration</h6>
                                {formData.level_config.map((level, index) => (
                                    <div key={index} className="card mb-3">
                                        <div className="card-header bg-light">
                                            <strong>Level {level.level}</strong>
                                        </div>
                                        <div className="card-body">
                                            <div className="row mb-2">
                                                <div className="col-md-8">
                                                    <label className="form-label">Level Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={level.level_name}
                                                        onChange={(e) =>
                                                            handleLevelConfigChange(index, 'level_name', e.target.value)
                                                        }
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Approval Type</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={level.require_all}
                                                            onChange={(e) =>
                                                                handleLevelConfigChange(index, 'require_all', e.target.checked)
                                                            }
                                                        />
                                                        <label className="form-check-label">
                                                            {level.require_all ? 'All Required' : 'Any One'}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            <label className="form-label fw-bold">Approvers *</label>
                                            <div className="row">
                                                {users.map(user => (
                                                    <div key={user.id} className="col-md-6 mb-2">
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={level.approvers.includes(user.id)}
                                                                onChange={() => handleApproverToggle(index, user.id)}
                                                            />
                                                            <label className="form-check-label">
                                                                {user.name} ({user.email})
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveWorkflow}
                                    disabled={loading}
                                >
                                    <i className="fa fa-save"></i> Save Workflow
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalWorkflows;
